<template>
  <div id="app" :class="{ 'qa-version-force-blocked': showVersionModal && versionTier === 'force' }">
    <nprogress-container></nprogress-container>
    <div
      v-if="!isProd && isLoggedIn"
      class="notification is-danger is-light has-text-centered mb-0"
      style="border-radius: 0"
    >
      You are using the
      <strong>{{ environment }}</strong>
      version of QuickAdmit. This is only for testing purposes. Please visit the
      <a href="https://app.quickadmit.com">Production Version</a>
      .
    </div>
    <Nav />
    <router-view />
    <UserNotificationModal
      v-if="userNotifications.length > 0"
      :notifications="userNotifications"
      @dismiss="onNotificationDismiss"
    />
    <MobileAppUpdateModal
      v-if="versionCheck && (versionCheck.tier === 'force' || versionCheck.tier === 'suggest')"
      :active="showVersionModal"
      :tier="versionTier"
      :platform="versionCheck.platform || 'unknown'"
      :ios-store-url="versionCheck.ios_store_url || ''"
      :android-store-url="versionCheck.android_store_url || ''"
      :message="versionCheck.message || ''"
      @later="onVersionUpdateLater"
    />
  </div>
</template>

<script>
import Nav from './components/Nav.vue';
import { mapGetters } from 'vuex';
import NprogressContainer from 'vue-nprogress/src/NprogressContainer';
import UserNotificationModal from './components/UserNotificationModal.vue';
import MobileAppUpdateModal from './components/MobileAppUpdateModal.vue';
import request from './request';

const SNOOZE_STORAGE_KEY = 'qa_mobile_update_snooze';
const MOBILE_USER_AGENT_FRAGMENT = 'QuickAdmitMobile/';

function normalizeVersionCheckPayload(data) {
  if (!data || typeof data !== 'object') {
    return { tier: 'none' };
  }
  const tier = data.tier === 'force' || data.tier === 'suggest' ? data.tier : 'none';
  return { ...data, tier };
}

function isSnoozed(check) {
  if (!check || check.tier !== 'suggest') {
    return false;
  }
  try {
    const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const snooze = JSON.parse(raw);
    if (
      snooze.minVersion !== check.minimum_supported_version ||
      snooze.latestVersion !== check.latest_version
    ) {
      localStorage.removeItem(SNOOZE_STORAGE_KEY);
      return false;
    }
    const snoozeDays = check.snooze_days || 1;
    const elapsed = Date.now() - snooze.dismissedAt;
    return elapsed < snoozeDays * 24 * 60 * 60 * 1000;
  } catch {
    localStorage.removeItem(SNOOZE_STORAGE_KEY);
    return false;
  }
}

export default {
  name: 'App',
  components: {
    Nav,
    NprogressContainer,
    UserNotificationModal,
    MobileAppUpdateModal,
  },
  data() {
    return {
      currentVersion: null,
      isNotified: false,
      userNotifications: [],
      versionCheck: null,
      versionCheckRequest: null,
      versionSnoozed: false,
    };
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'isProd', 'environment']),
    versionTier() {
      if (!this.versionCheck || this.versionCheck.tier === 'none') {
        return 'suggest';
      }
      return this.versionCheck.tier;
    },
    showVersionModal() {
      if (!this.versionCheck || this.versionCheck.tier === 'none') {
        return false;
      }
      if (this.versionCheck.tier === 'force') {
        return true;
      }
      if (this.versionCheck.tier === 'suggest' && !this.versionSnoozed) {
        return true;
      }
      return false;
    },
  },
  watch: {
    isLoggedIn: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          await this.fetchNotifications();
        }
      },
    },
  },
  async mounted() {
    if (navigator.userAgent.includes(MOBILE_USER_AGENT_FRAGMENT)) {
      await this.runVersionCheck();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.applyDevVersionPreview();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    async runVersionCheck() {
      if (this.versionCheckRequest) {
        await this.versionCheckRequest;
        return;
      }
      this.versionCheckRequest = request.get('/public/mobile-app-version-check');
      try {
        const { data } = await this.versionCheckRequest;
        this.versionCheck = normalizeVersionCheckPayload(data);
        this.versionSnoozed = isSnoozed(this.versionCheck);
      } catch {
        this.versionCheck = { tier: 'none' };
      } finally {
        this.versionCheckRequest = null;
      }
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.runVersionCheck();
      }
    },
    // Non-production only: open any page with ?qa_preview_app_update=force|suggest to preview the modal.
    // Optional: &qa_preview_platform=ios|android|unknown (default ios).
    applyDevVersionPreview() {
      if (this.isProd) {
        return;
      }
      const q = this.$route.query.qa_preview_app_update;
      if (q !== 'force' && q !== 'suggest') {
        return;
      }
      let platform = this.$route.query.qa_preview_platform;
      if (platform !== 'ios' && platform !== 'android' && platform !== 'unknown') {
        platform = 'ios';
      }
      const base = this.versionCheck && typeof this.versionCheck === 'object' ? this.versionCheck : {};
      this.versionCheck = {
        ...base,
        tier: q,
        platform,
        minimum_supported_version: base.minimum_supported_version || '1.0.0',
        latest_version: base.latest_version || '2.0.0',
        ios_store_url: base.ios_store_url || 'https://apps.apple.com/',
        android_store_url: base.android_store_url || 'https://play.google.com/',
        message: base.message || 'Development preview of the app update modal.',
        client_version: base.client_version || '0.0.0-preview',
        snooze_days: base.snooze_days || 1,
      };
      this.versionSnoozed = false;
    },
    onVersionUpdateLater() {
      if (this.versionCheck && this.versionCheck.tier === 'suggest') {
        try {
          localStorage.setItem(
            SNOOZE_STORAGE_KEY,
            JSON.stringify({
              dismissedAt: Date.now(),
              minVersion: this.versionCheck.minimum_supported_version,
              latestVersion: this.versionCheck.latest_version,
            }),
          );
        } catch {
          // localStorage unavailable; snooze won't persist but modal will still dismiss.
        }
      }
      this.versionSnoozed = true;
    },
    async fetchNotifications() {
      try {
        const response = await request.get('/user/notifications');
        this.userNotifications = response.data.notifications || [];
      } catch (e) {
        this.userNotifications = [];
      }
    },
    onNotificationDismiss(id) {
      this.userNotifications = this.userNotifications.filter((n) => n.id !== id);
    },
  },
};
</script>

<style>
#app {
  min-height: 100vh;
}
.qa-version-force-blocked > *:not(.modal) {
  pointer-events: none;
  user-select: none;
  opacity: 0.35;
}
</style>
